<template>
  <nav v-if="items.length > 1" class="app-breadcrumb" aria-label="Fil d'Ariane">
    <ol class="breadcrumb mb-3">
      <li
        v-for="(item, index) in items"
        :key="`${item.label}-${index}`"
        class="breadcrumb-item"
        :class="{ active: index === items.length - 1 }"
        :aria-current="index === items.length - 1 ? 'page' : undefined"
      >
        <router-link v-if="index < items.length - 1 && item.to" :to="item.to">
          {{ item.label }}
        </router-link>
        <span v-else>{{ item.label }}</span>
      </li>
    </ol>
  </nav>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

type RouteTarget = RouteLocationRaw;

interface BreadcrumbItem {
  label: string;
  to?: RouteTarget;
}

const trails: Record<string, BreadcrumbItem[]> = {
  Dashboard: [{ label: 'Tableau de bord' }],
  Sites: [{ label: 'Mes sites' }],
  SiteShow: [{ label: 'Mes sites', to: { name: 'Sites' } }, { label: 'Détail du site' }],
  SettingsGeneral: [{ label: 'Paramètres' }, { label: 'Général' }],
  SettingsPrinting: [{ label: 'Paramètres' }, { label: 'Impression' }],
  SettingsInventory: [{ label: 'Paramètres' }, { label: 'Inventaire' }],
  SettingsAccounting: [{ label: 'Paramètres' }, { label: 'Comptabilité' }],
  SettingsEcommerce: [{ label: 'Paramètres' }, { label: 'E-commerce' }],
  ShopInfo: [{ label: 'Paramètres' }, { label: 'Infos boutique' }],
  DeliverySettings: [{ label: 'Paramètres' }, { label: 'Réglages des livraisons' }],
  MovementSettings: [{ label: 'Paramètres' }, { label: 'Réglages mouvements' }],
  Users: [{ label: 'Utilisateurs' }],
  ActivityLogs: [{ label: 'Utilisateurs' }, { label: 'Activités récentes' }],
  UserShow: [{ label: 'Utilisateurs', to: { name: 'Users' } }, { label: 'Détail utilisateur' }],
  ProductDatabase: [{ label: 'Stock' }, { label: 'Base de donnée' }],
  AllSitesProductDatabase: [{ label: 'Mes sites', to: { name: 'Sites' } }, { label: 'Base de donnée' }],
  AllSitesUsers: [{ label: 'Mes sites', to: { name: 'Sites' } }, { label: 'Utilisateurs' }],
  AllSitesUserShow: [
    { label: 'Mes sites', to: { name: 'Sites' } },
    { label: 'Utilisateurs', to: { name: 'AllSitesUsers' } },
    { label: 'Détail utilisateur' },
  ],
  Products: [{ label: 'Stock' }, { label: 'Produits' }],
  ProductShow: [{ label: 'Stock' }, { label: 'Produits', to: { name: 'Products' } }, { label: 'Détail produit' }],
  StockMovements: [{ label: 'Stock' }, { label: 'Mouvements' }],
  ElectronicWallets: [{ label: 'Stock' }, { label: 'Portefeuilles électroniques' }],
  StockCalendar: [{ label: 'Stock' }, { label: 'Calendrier' }],
  StockTransfers: [{ label: 'Stock' }, { label: 'Transferts' }],
  StockTransferCreate: [{ label: 'Stock' }, { label: 'Transferts', to: { name: 'StockTransfers' } }, { label: 'Nouveau transfert' }],
  StockTransferEdit: [{ label: 'Stock' }, { label: 'Transferts', to: { name: 'StockTransfers' } }, { label: 'Modifier le transfert' }],
  StockTransferShow: [{ label: 'Stock' }, { label: 'Transferts', to: { name: 'StockTransfers' } }, { label: 'Détail du transfert' }],
  Inventories: [{ label: 'Stock' }, { label: 'Inventaires' }],
  InventoryShow: [{ label: 'Stock' }, { label: 'Inventaires', to: { name: 'Inventories' } }, { label: 'Détail inventaire' }],
  InventoryCount: [{ label: 'Stock' }, { label: 'Inventaires', to: { name: 'Inventories' } }, { label: 'Comptage inventaire' }],
  Rays: [{ label: 'Stock' }, { label: 'Rayons' }],
  RayShow: [{ label: 'Stock' }, { label: 'Rayons', to: { name: 'Rays' } }, { label: 'Détail rayon' }],
  RayProductAssignment: [{ label: 'Stock' }, { label: 'Rayons', to: { name: 'Rays' } }, { label: 'Associer des produits' }],
  Categories: [{ label: 'Stock' }, { label: 'Catégories' }],
  CategoryShow: [{ label: 'Stock' }, { label: 'Catégories', to: { name: 'Categories' } }, { label: 'Détail catégorie' }],
  CategoryProductAssignment: [{ label: 'Stock' }, { label: 'Catégories', to: { name: 'Categories' } }, { label: 'Associer des produits' }],
  Sales: [{ label: 'Ventes' }, { label: 'Historique ventes' }],
  SaleShow: [{ label: 'Ventes' }, { label: 'Historique ventes', to: { name: 'Sales' } }, { label: 'Détail vente' }],
  CashRegisters: [{ label: 'Ventes' }, { label: 'Caisse' }],
  CashRegisterShow: [{ label: 'Ventes' }, { label: 'Caisse', to: { name: 'CashRegisters' } }, { label: 'Détail caisse' }],
  CashCounts: [{ label: 'Ventes' }, { label: 'Billetage' }],
  CashCountShow: [{ label: 'Ventes' }, { label: 'Billetage', to: { name: 'CashCounts' } }, { label: 'Détail billetage' }],
  CashierEndOfDay: [{ label: 'Caisse' }, { label: 'Fin de journée' }],
  Purchases: [{ label: 'Achats' }, { label: "Commandes d'achat" }],
  PurchaseShow: [{ label: 'Achats' }, { label: "Commandes d'achat", to: { name: 'Purchases' } }, { label: "Détail de l'achat" }],
  Expenses: [{ label: 'Dépenses' }, { label: 'Liste' }],
  ExpenseShow: [{ label: 'Dépenses', to: { name: 'Expenses' } }, { label: 'Détail' }],
  ExpenseEdit: [{ label: 'Dépenses', to: { name: 'Expenses' } }, { label: 'Modifier' }],
  ExpenseTypes: [{ label: 'Paramètres' }, { label: 'Types de dépenses' }],
  SupplierReturns: [{ label: 'Achats' }, { label: 'Retours fournisseur' }],
  SupplierReturnCreate: [{ label: 'Achats' }, { label: 'Retours fournisseur', to: { name: 'SupplierReturns' } }, { label: 'Nouveau retour' }],
  SupplierReturnEdit: [{ label: 'Achats' }, { label: 'Retours fournisseur', to: { name: 'SupplierReturns' } }, { label: 'Modifier le retour' }],
  SupplierReturnShow: [{ label: 'Achats' }, { label: 'Retours fournisseur', to: { name: 'SupplierReturns' } }, { label: 'Détail du retour' }],
  Suppliers: [{ label: 'Achats' }, { label: 'Fournisseurs' }],
  SupplierShow: [{ label: 'Achats' }, { label: 'Fournisseurs', to: { name: 'Suppliers' } }, { label: 'Détail fournisseur' }],
  Clients: [{ label: 'Clients' }],
  ClientShow: [{ label: 'Clients', to: { name: 'Clients' } }, { label: 'Fiche client' }],
  DeliveriesView: [{ label: 'Ventes' }, { label: 'Livraisons' }],
  DeliveryShow: [
    { label: 'Ventes' },
    { label: 'Livraisons', to: { name: 'DeliveriesView' } },
    { label: 'Détail livraison' },
  ],
  AdminManual: [{ label: 'Manuel d’utilisation' }, { label: 'Admin' }],
  CashierManual: [{ label: 'Manuel d’utilisation' }, { label: 'Caissier' }],
  SalesReport: [{ label: 'Rapports' }, { label: 'Ventes' }],
  WalletsReport: [{ label: 'Rapports' }, { label: 'Portefeuilles électroniques' }],
  RevenueMarginsReport: [{ label: 'Rapports' }, { label: 'Chiffre d’affaires & marges' }],
  PurchasesReport: [{ label: 'Rapports' }, { label: 'Achats' }],
  ExpensesReport: [{ label: 'Rapports' }, { label: 'Dépenses' }],
  InventoriesReport: [{ label: 'Rapports' }, { label: 'Inventaires' }],
  ProductsReport: [{ label: 'Rapports' }, { label: 'Produits' }],
  SupplierReturnsReport: [{ label: 'Rapports' }, { label: 'Retours fournisseur' }],
  SupplierPaymentsReport: [{ label: 'Rapports' }, { label: 'Paiements fournisseur' }],
  StockMovementsReport: [{ label: 'Rapports' }, { label: 'Mouvements de stock' }],
  StockTransfersReport: [{ label: 'Rapports' }, { label: 'Transferts de stock' }],
  CashRegistersReport: [{ label: 'Rapports' }, { label: 'Sessions de caisse' }],
  DeliveriesReport: [{ label: 'Rapports' }, { label: 'Livraisons' }],
  AccountingJournalReport: [{ label: 'Rapports' }, { label: 'Journal comptable' }],
  AccountingLedgerReport: [{ label: 'Rapports' }, { label: 'Grand livre' }],
  AccountingBalanceReport: [{ label: 'Rapports' }, { label: 'Balance comptable' }],
  AccountingExceptionsReport: [{ label: 'Rapports' }, { label: 'Anomalies comptables' }],
};

export default defineComponent({
  name: 'AppBreadcrumbs',
  computed: {
    previousRouteTarget(): RouteTarget | null {
      if (typeof window === 'undefined') return null;

      const state = window.history.state as { back?: unknown } | null;
      const back = state?.back;

      if (typeof back !== 'string' || !back || back === this.$route.fullPath) {
        return null;
      }

      return back;
    },

    items(): BreadcrumbItem[] {
      const items = (trails[String(this.$route.name)] || []).map((item) => ({ ...item }));
      const previousIndex = items.length - 2;

      if (
        previousIndex >= 0 &&
        !items[previousIndex].to &&
        this.previousRouteTarget
      ) {
        items[previousIndex].to = this.previousRouteTarget;
      }

      return items;
    },
  },
});
</script>

<style scoped>
.app-breadcrumb {
  font-size: 0.82rem;
}

.app-breadcrumb .breadcrumb {
  --bs-breadcrumb-divider-color: rgba(33, 37, 41, 0.45);
}
</style>
